import { useEffect, useRef, useState, type ReactNode } from 'react'

interface Props {
  children: ReactNode
  className?: string
}

/**
 * Fades + slides its children in the first time they scroll into view,
 * mirroring the mock's IntersectionObserver reveal on the featured card,
 * story cards, "Why It Works" band and CTA. Decorative only — the motion is
 * defined in `.cs-reveal` CSS, which is disabled under prefers-reduced-motion.
 */
function Reveal({ children, className = '' }: Props): JSX.Element {
  const ref = useRef<HTMLDivElement>(null)
  const [inView, setInView] = useState(false)

  useEffect(() => {
    const el = ref.current
    if (!el) return

    const observer = new IntersectionObserver(
      entries =>
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setInView(true)
            observer.disconnect()
          }
        }),
      { threshold: 0.1 }
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  return (
    <div ref={ref} data-in={inView} className={`cs-reveal ${className}`}>
      {children}
    </div>
  )
}

export default Reveal
